import { useSyncExternalStore } from 'react';
import {
  MAX_CART_LINES,
  MAX_LINE_QUANTITY,
  type CartComboPick,
  type CartInput,
  type CountryCode,
  type MenuFoodType,
} from '@capsico/contracts';

/**
 * The cart (one restaurant at a time), kept on this phone so it survives a reload. It is not a
 * secret and holds no prices the server trusts: every bill comes from the server quote. The
 * shown line prices here are only an estimate for the cart bar until the quote arrives.
 */
export interface CartLine {
  /** Same dish + size + add-ons + combo choices = same line (quantities add up). */
  key: string;
  itemId: string;
  name: string;
  foodType: MenuFoodType;
  variantId: string | null;
  variantName: string | null;
  addonOptionIds: string[];
  addonNames: string[];
  comboPicks: CartComboPick[];
  /** Price of one, as shown on the menu (estimate). */
  unitPriceMinor: number;
  quantity: number;
  note: string | null;
}

export interface Cart {
  outletId: string;
  outletName: string;
  country: CountryCode;
  lines: CartLine[];
}

export type NewLine = Omit<CartLine, 'key' | 'quantity'> & { quantity: number };

const KEY = 'capsico:customer:cart';

function lineKey(
  line: Pick<CartLine, 'itemId' | 'variantId' | 'addonOptionIds' | 'comboPicks'>,
): string {
  const addons = [...line.addonOptionIds].sort().join('+');
  const combo = [...line.comboPicks]
    .sort((a, b) => a.slotId.localeCompare(b.slotId))
    .map((pick) => `${pick.slotId}:${[...pick.itemIds].sort().join('+')}`)
    .join('|');
  return `${line.itemId}/${line.variantId ?? '-'}/${addons}/${combo}`;
}

function read(): Cart | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<Cart>;
    if (
      typeof value.outletId !== 'string' ||
      typeof value.outletName !== 'string' ||
      (value.country !== 'IN' && value.country !== 'NP') ||
      !Array.isArray(value.lines)
    ) {
      return null;
    }
    const lines = value.lines.filter(
      (line): line is CartLine =>
        typeof line?.itemId === 'string' &&
        typeof line.key === 'string' &&
        typeof line.quantity === 'number' &&
        line.quantity >= 1,
    );
    return lines.length > 0 ? { ...(value as Cart), lines } : null;
  } catch {
    return null;
  }
}

let current: Cart | null = typeof window === 'undefined' ? null : read();
const listeners = new Set<() => void>();

function commit(next: Cart | null): void {
  current = next && next.lines.length > 0 ? next : null;
  try {
    if (current) window.localStorage.setItem(KEY, JSON.stringify(current));
    else window.localStorage.removeItem(KEY);
  } catch {
    // Storage blocked: the cart still works for this visit.
  }
  for (const listener of listeners) listener();
}

/** Result of adding: added, or the cart holds another restaurant and the customer must choose. */
export type AddResult = 'added' | 'other_restaurant' | 'full';

export const cartStore = {
  get: (): Cart | null => current,

  /** Adds a dish. Refuses (without changing anything) when the cart is from another restaurant. */
  add(outlet: { id: string; name: string; country: CountryCode }, line: NewLine): AddResult {
    if (current && current.outletId !== outlet.id) return 'other_restaurant';
    const key = lineKey(line);
    const lines = current ? [...current.lines] : [];
    const at = lines.findIndex((entry) => entry.key === key);
    if (at >= 0) {
      const existing = lines[at] as CartLine;
      lines[at] = {
        ...existing,
        quantity: Math.min(MAX_LINE_QUANTITY, existing.quantity + line.quantity),
      };
    } else {
      if (lines.length >= MAX_CART_LINES) return 'full';
      lines.push({ ...line, key, quantity: Math.min(MAX_LINE_QUANTITY, line.quantity) });
    }
    commit({ outletId: outlet.id, outletName: outlet.name, country: outlet.country, lines });
    return 'added';
  },

  /** Empties the cart and starts a new one at this restaurant ("Start a new cart?" → Yes). */
  replace(outlet: { id: string; name: string; country: CountryCode }, line: NewLine): void {
    commit(null);
    cartStore.add(outlet, line);
  },

  setQuantity(key: string, quantity: number): void {
    if (!current) return;
    const lines = current.lines
      .map((line) =>
        line.key === key ? { ...line, quantity: Math.min(MAX_LINE_QUANTITY, quantity) } : line,
      )
      .filter((line) => line.quantity > 0);
    commit({ ...current, lines });
  },

  /** Removes one of the most recently added line for this dish (menu − button). */
  removeOneOfItem(itemId: string): void {
    if (!current) return;
    const lines = [...current.lines];
    for (let i = lines.length - 1; i >= 0; i -= 1) {
      const line = lines[i] as CartLine;
      if (line.itemId === itemId) {
        lines[i] = { ...line, quantity: line.quantity - 1 };
        break;
      }
    }
    commit({ ...current, lines: lines.filter((line) => line.quantity > 0) });
  },

  clear(): void {
    commit(null);
  },
};

export function useCart(): Cart | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => null,
  );
}

/** What the server needs: the cart without the display-only fields. */
export function cartInput(cart: Cart): CartInput {
  return {
    outletId: cart.outletId,
    lines: cart.lines.map((line) => ({
      itemId: line.itemId,
      variantId: line.variantId,
      addonOptionIds: line.addonOptionIds,
      comboPicks: line.comboPicks,
      quantity: line.quantity,
      note: line.note,
    })),
  };
}

/** Items and estimated total for the cart bar (the bill itself always comes from the server). */
export function cartSummary(cart: Cart | null): { count: number; estimateMinor: number } {
  if (!cart) return { count: 0, estimateMinor: 0 };
  return cart.lines.reduce(
    (sum, line) => ({
      count: sum.count + line.quantity,
      estimateMinor: sum.estimateMinor + line.unitPriceMinor * line.quantity,
    }),
    { count: 0, estimateMinor: 0 },
  );
}

/** How many of a dish are in the cart (all sizes and add-ons together). */
export function quantityOf(cart: Cart | null, itemId: string): number {
  if (!cart) return 0;
  return cart.lines.reduce((sum, line) => sum + (line.itemId === itemId ? line.quantity : 0), 0);
}
